import React, { useState } from "react";
import { Form, Input, Button, message } from "antd";
import { useNavigate } from "react-router-dom";
import { MailOutlined, LockOutlined, PhoneOutlined } from "@ant-design/icons";
import { loginUser } from "../../ApiServices/SignInApi";
import "./SignIn.css";
import { jwtDecode } from "jwt-decode";
import Logo from "../../Components/Logo/Logo";
import LoginFlag from "../../Pages/LoginFlag/LoginFlag";

function SignIn({ setLoggedIn, deviceId, userIp, setProfileImage }) {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [showSecurityModal, setShowSecurityModal] = useState(false);
  const [securityData, setSecurityData] = useState(null);
  const [isPhoneLogin, setIsPhoneLogin] = useState(false);
  const [phoneNumber, setPhoneNumber] = useState("");

  const handlePhoneNumberChange = (e) => {
    let value = e.target.value.replace(/\D/g, ''); // Remove all non-digit characters
    
    // If user starts with 0, replace with 255
    if (value.startsWith('0')) {
      value = '255' + value.substring(1);
    }
    // If user doesn't start with country code, add it (unless empty)
    else if (value && !value.startsWith('255')) {
      value = '255' + value;
    }
    
    // Limit to 12 digits total (255 + 9 digits)
    if (value.length > 12) {
      value = value.substring(0, 12);
    }
    
    setPhoneNumber(value);
  };

  const formatPhoneNumberForDisplay = (value) => {
    if (!value) return '';
    
    // If starts with 255, show as 255 XXX XXX XXX
    if (value.startsWith('255') && value.length > 3) {
      const rest = value.substring(3);
      return `255 ${rest.substring(0, 3)} ${rest.substring(3, 6)} ${rest.substring(6)}`;
    }
    
    return value;
  };

  const handleSignin = async (values) => {
    setLoading(true);
    try {
      let username;
      
      // Check if the input is a phone number
      if (values.email.match(/^\d+$/)) {
        // Process phone number
        let phone = values.email.replace(/\D/g, '');
        if (phone.startsWith('0')) {
          phone = '255' + phone.substring(1);
        } else if (!phone.startsWith('255')) {
          phone = '255' + phone;
        }
        username = phone;
        setIsPhoneLogin(true);
      } else {
        // Process email
        username = values.email.trim();
        setIsPhoneLogin(false);
      }

      const credentials = {
        username,
        password: values.password,
      };

      const response = await loginUser(credentials, deviceId, userIp);
      
      if (response.code === 200) {
        handleSuccessfulLogin(response);
      } else if (response.code === 205) {
        handleSecurityCheck(response, username);
      } else if (response.code === 423) {
        handleAccountLocked(response, username);
      }  else if (response.code === 412) {
        handleAccountLocked();
      } else {
        message.error(response.message || "Login failed");
      }
    } catch (error) {
      handleLoginError(error);
    } finally {
      setLoading(false);
    }
  };

  const handleSuccessfulLogin = (response) => {
    const { jwt: token, photo } = response.data;
    const decodedToken = jwtDecode(token);
    console.log(response.data)
    
    // Store user data
    localStorage.setItem("accessToken", token);
    localStorage.setItem("userPhoto", photo);
    setProfileImage(photo);
    
    localStorage.setItem("userInfo", JSON.stringify({
      name: decodedToken.name,
      username: decodedToken.username,
      role: decodedToken.role,
      exp: decodedToken.exp,
      userId: decodedToken.userId
    }));

    setLoggedIn(true);
    message.success("Login successful!");
    redirectUser(decodedToken.role.toLowerCase(), decodedToken);
  };

  const redirectUser = (role, userData) => {
    const routes = {
      admin: "/admin-page/dashboard",
      teacher: {
        path: "/teacher-home",
        state: { userId: userData.userId, name: userData.name }
      },
      default: "/courses"
    };

    if (role === "teacher") {
      navigate(routes.teacher.path, { state: routes.teacher.state });
    } else {
      navigate(routes[role] || routes.default);
    }
  };

  const handleSecurityCheck = (response, username) => {
    setSecurityData({
      message: response.message,
      email: isPhoneLogin ? undefined : username,
      phoneNumber: isPhoneLogin ? username : undefined,
      deviceId,
      userIp
    });
    setShowSecurityModal(true);
  };

  const handleAccountLocked = (response, username) => {
    message.warning(response.message || "Account needs verification");
    navigate("/verify-otp", {
      state: {
        email: isPhoneLogin ? undefined : username,
        phoneNumber: isPhoneLogin ? username : undefined,
        deviceId,
        userIp
      }
    });
  };

  const handleLoginError = (error) => {
    console.error("Login error:", error);
    const errorMessage = error.response?.data?.message || 
                        error.message || 
                        "An error occurred during login";
    message.error(errorMessage);
  };

  const handleReset = () => navigate("/reset");

  const handleVerifySuccess = () => {
    setShowSecurityModal(false);
    message.success("Account verified successfully! You can now login.");
  };

  const validateUsername = (_, value) => {
    if (!value) {
      return Promise.reject('Please enter your email or phone number');
    }

    // Check if it's a phone number (all digits)
    if (value.match(/^\d+$/)) {
      const digits = value.replace(/\D/g, '');
      // Validate phone number format
      if (
        (digits.startsWith('255') && digits.length !== 12) ||
        (digits.startsWith('0') && digits.length !== 10) ||
        (!digits.startsWith('255') && !digits.startsWith('0') && digits.length !== 9)
      ) {
        return Promise.reject('Please enter a valid phone number (255XXXXXXXXX or 0XXXXXXXXX)');
      }
      return Promise.resolve();
    }

    // Validate email format
    if (value.trim() !== value) {
      return Promise.reject('Remove spaces from email');
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) {
      return Promise.reject('Please enter a valid email');
    }
    
    return Promise.resolve();
  };

  return (
    <div className="signin-page-container">
      <div className="signin-container">
        <Logo />
        <h4>Sign in to your account</h4>
        <span className="signin-text">Welcome Back! Login with your credentials</span>

        <Form className="signin-form" layout="vertical" onFinish={handleSignin}>
          <Form.Item
            name="email"
            rules={[
              { required: true, message: "Please enter your email or phone number" },
              { validator: validateUsername }
            ]}
            normalize={(value) => value.trim()} // Auto-trim on form submission
          >
            <Input 
              prefix={<MailOutlined />} 
              placeholder="Email or Phone Number (255XXXXXXXXX or 0XXXXXXXXX)" 
              className="signin-input"
              onBlur={(e) => {
                const value = e.target.value.trim();
                e.target.value = value;
                // If it's a phone number, format it
                if (value.match(/^\d+$/)) {
                  handlePhoneNumberChange({ target: { value } });
                }
              }}
            />
          </Form.Item>

          <Form.Item
            name="password"
            rules={[
              { required: true, message: "Please enter your password" },
              { min: 6, message: "Password must be at least 6 characters" },
            ]}
          >
            <Input.Password 
              prefix={<LockOutlined />} 
              placeholder="Password" 
              className="signin-input" 
            />
          </Form.Item>

          <span onClick={handleReset} className="signin-reset-password">
            <span className="forget-password">Forgot your password? </span> Reset Password
          </span>


          <Button 
            type="primary" 
            htmlType="submit" 
            className="signin-button" 
            loading={loading}
            disabled={loading}
          >
            {loading ? "Signing In..." : "Sign In"}
          </Button>
        </Form>

        <p className="signin-link">
          Don't have an account? <a href="/signUp">Sign Up</a>
        </p>

        {showSecurityModal && (
          <div className="security-modal-overlay">
            <div className="security-modal-content">
              <LoginFlag 
                email={securityData?.email}
                deviceId={deviceId}
                userIp={userIp}
                userId={securityData?.userId}
                setLoggedIn={setLoggedIn}
                onVerificationSuccess={handleVerifySuccess}
                onClose={() => setShowSecurityModal(false)}
              />
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default SignIn;